import React from 'react';
import PropTypes from 'prop-types';
import * as SelectPrimitive from '@radix-ui/react-select';
import Check from 'lucide-react/dist/esm/icons/check';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up';
import { cn } from '~/lib/utils';
import { formControlClassName } from './form-control';

const Select = SelectPrimitive.Root;
const SelectValue = SelectPrimitive.Value;
const SelectGroup = SelectPrimitive.Group;

const SelectTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Trigger
		ref={ref}
		className={cn(
			formControlClassName,
			'justify-between font-medium focus:border-ring focus:bg-card focus:outline-none focus:ring-0 focus-visible:border-ring focus-visible:bg-card focus-visible:outline-none focus-visible:ring-0 data-[disabled]:pointer-events-none data-[disabled]:bg-muted data-[disabled]:text-muted-foreground data-[disabled]:opacity-60',
			className
		)}
		{...props}
	>
		{children}
		<SelectPrimitive.Icon asChild>
			<ChevronDown className="h-4 w-4 opacity-70" />
		</SelectPrimitive.Icon>
	</SelectPrimitive.Trigger>
));

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
SelectTrigger.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node
};

const SelectContent = React.forwardRef(({
	className,
	children,
	position = 'popper',
	style,
	...props
}, ref) => (
	<SelectPrimitive.Portal>
		<SelectPrimitive.Content
			ref={ref}
			className={cn(
				'relative max-h-96 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
				position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
				className
			)}
			// Above DialogOverlay/DialogContent (200/201) so dropdowns inside
			// dialogs aren't hidden behind them; matches Popover's z-[2000].
			style={{ zIndex: 2000, minWidth: 'var(--radix-select-trigger-width)', ...style }}
			position={position}
			{...props}
		>
			<SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
				<ChevronUp className="h-4 w-4" />
			</SelectPrimitive.ScrollUpButton>
			<SelectPrimitive.Viewport
				className="w-full p-1"
				style={position === 'popper' ? { minWidth: 'var(--radix-select-trigger-width)' } : undefined}
			>
				{children}
			</SelectPrimitive.Viewport>
			<SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
				<ChevronUp className="h-4 w-4 rotate-180" />
			</SelectPrimitive.ScrollDownButton>
		</SelectPrimitive.Content>
	</SelectPrimitive.Portal>
));

SelectContent.displayName = SelectPrimitive.Content.displayName;
SelectContent.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node,
	position: PropTypes.string,
	style: PropTypes.object
};

const SelectItem = React.forwardRef(({ className, children, ...props }, ref) => (
	<SelectPrimitive.Item
		ref={ref}
		className={cn(
			'relative flex w-full cursor-default select-none items-center rounded-sm py-2 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
			className
		)}
		{...props}
	>
		<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
			<SelectPrimitive.ItemIndicator>
				<Check className="h-4 w-4" />
			</SelectPrimitive.ItemIndicator>
		</span>

		<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
	</SelectPrimitive.Item>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;
SelectItem.propTypes = {
	className: PropTypes.string,
	children: PropTypes.node
};

export {
	Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue
};
